import React, { useMemo, useCallback, useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import type { BlockDocumentContent, Block } from "@unruly-global/api-types";
import {
  buildLocationClusters,
  type LocationCluster,
  type LocationInput,
} from "../../helper/resolveLocation";
import styles from "./BlockDocumentMap.module.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BlockDocumentMapProps {
  document: BlockDocumentContent;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract location inputs from content blocks. */
function extractLocationInputs(blocks: Block[]): LocationInput[] {
  const inputs: LocationInput[] = [];
  for (const block of blocks) {
    if ("location" in block && block.location) {
      inputs.push({
        uid: block.uid,
        location: block.location,
        title: "title" in block ? (block.title as string) : undefined,
        blurb:
          "location_blurb" in block
            ? (block.location_blurb as string)
            : undefined,
      });
    }
  }
  return inputs;
}

// ---------------------------------------------------------------------------
// Auto-zoom to fit all markers
// ---------------------------------------------------------------------------

const FitBounds: React.FC<{ clusters: LocationCluster[] }> = ({ clusters }) => {
  const map = useMap();

  useEffect(() => {
    if (clusters.length === 0) return;

    const bounds = L.latLngBounds(
      clusters.map((c) => [c.coords[0], c.coords[1]] as L.LatLngTuple),
    );
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
  }, [map, clusters]);

  return null;
};

// ---------------------------------------------------------------------------
// Expose map ref
// ---------------------------------------------------------------------------

const MapController: React.FC<{
  mapRef: React.MutableRefObject<L.Map | null>;
}> = ({ mapRef }) => {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);

  return null;
};

// ---------------------------------------------------------------------------
// Marker
// ---------------------------------------------------------------------------

const MARKER_RADIUS = 7;
const MARKER_COLOR = "#4c90f0";

interface DotMarkerProps {
  cluster: LocationCluster;
  onTitleClick: (blockUid: string) => void;
}

const DotMarker: React.FC<DotMarkerProps> = ({ cluster, onTitleClick }) => {
  const markerRef = useRef<L.CircleMarker>(null);

  return (
    <CircleMarker
      ref={markerRef}
      center={[cluster.coords[0], cluster.coords[1]]}
      radius={MARKER_RADIUS}
      pathOptions={{
        fillColor: MARKER_COLOR,
        color: "white",
        weight: 2,
        fillOpacity: 0.85,
      }}
      eventHandlers={{
        mouseover: () => markerRef.current?.openPopup(),
      }}
    >
      <Popup offset={[0, -8]} closeButton={false}>
        <div className={styles.popup}>
          <strong className={styles.popupLabel}>{cluster.label}</strong>
          {cluster.blockBlurbs.some(Boolean) && (
            <ul className={styles.popupList}>
              {cluster.blockBlurbs.map((blurb, i) =>
                blurb ? (
                  <li key={i} className={styles.popupListItem}>
                    <button
                      type="button"
                      className={styles.popupLink}
                      onClick={() => onTitleClick(cluster.titleUids[i])}
                    >
                      {blurb}
                    </button>
                  </li>
                ) : null,
              )}
            </ul>
          )}
        </div>
      </Popup>
    </CircleMarker>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const BlockDocumentMap: React.FC<BlockDocumentMapProps> = ({
  document: doc,
}) => {
  const locationInputs = useMemo(
    () => extractLocationInputs(doc.blocks),
    [doc.blocks],
  );

  const clusters = useMemo(
    () => buildLocationClusters(locationInputs),
    [locationInputs],
  );

  const mapRef = useRef<L.Map | null>(null);

  const handleTitleClick = useCallback(
    (blockUid: string) => {
      const el = document.getElementById(`block-${blockUid}`);
      if (!el) return;
      const map = mapRef.current;
      if (map) {
        map.closePopup();
        const bounds = L.latLngBounds(
          clusters.map((c) => [c.coords[0], c.coords[1]] as L.LatLngTuple),
        );
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
      }
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
      el.classList.add("bdm-highlight");
      setTimeout(() => {
        el.classList.remove("bdm-highlight");
        el.classList.add("bdm-highlight-fade");
        setTimeout(() => el.classList.remove("bdm-highlight-fade"), 800);
      }, 1200);
    },
    [clusters],
  );

  // Don't render the map if there are no resolvable locations
  if (clusters.length === 0) return null;

  return (
    <div className={styles.wrapper} data-no-copy>
      <div className={styles.mapContainer}>
        <MapContainer
          center={[20, 10]}
          zoom={2}
          className={styles.map}
          scrollWheelZoom={false}
          zoomControl={true}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          />
          <FitBounds clusters={clusters} />
          <MapController mapRef={mapRef} />
          {clusters.map((cluster) => (
            <DotMarker
              key={cluster.label}
              cluster={cluster}
              onTitleClick={handleTitleClick}
            />
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default BlockDocumentMap;
